import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Avatar,
  alpha,
} from '@mui/material';
import {
  Warning,
  Assignment,
  Close,
  Schedule,
  TrendingUp,
} from '@mui/icons-material';
import { Lead } from '../types';

interface LeadAssignmentConfirmationModalProps {
  open: boolean;
  lead: Lead | null;
  leadsAssignedThisHour: number;
  onClose: () => void;
  onConfirm: () => void;
  isMobile?: boolean;
}

const LeadAssignmentConfirmationModal: React.FC<LeadAssignmentConfirmationModalProps> = ({
  open,
  lead,
  leadsAssignedThisHour,
  onClose,
  onConfirm,
}) => {
  if (!lead) return null;

  const isAtLimit = leadsAssignedThisHour >= 5;
  const remaining = Math.max(0, 5 - leadsAssignedThisHour);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        background: isAtLimit
          ? 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)'
          : 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
        color: 'white',
      }}>
        <Warning sx={{ mr: 1 }} />
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {isAtLimit ? 'Limite Atingido!' : 'Confirmar Atribuição'}
        </Typography>
        <Button
          onClick={onClose}
          sx={{
            color: 'white',
            minWidth: 'auto',
            p: 1,
          }}
        >
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 3 }}>
        {/* Lead Information */}
        <Box sx={{
          p: 2,
          backgroundColor: alpha('#2196f3', 0.1),
          borderRadius: 2,
          border: `1px solid ${alpha('#2196f3', 0.2)}`,
          mb: 3,
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            {lead.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            📱 {lead.phoneNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            📧 {lead.email}
          </Typography>
        </Box>

        {/* Hour Limit Information */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{
              backgroundColor: isAtLimit ? '#f44336' : '#ff9800',
              mr: 2,
              width: 40,
              height: 40
            }}>
              <Schedule />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Controle Horário de Leads
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Você já atribuiu <strong>{leadsAssignedThisHour}</strong> de <strong>5</strong> leads nesta hora
              </Typography>
            </Box>
          </Box>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            backgroundColor: alpha(isAtLimit ? '#f44336' : '#4caf50', 0.1),
            borderRadius: 2,
            border: `1px solid ${alpha(isAtLimit ? '#f44336' : '#4caf50', 0.2)}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TrendingUp sx={{
                mr: 1,
                color: isAtLimit ? '#f44336' : '#4caf50'
              }} />
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {isAtLimit ? 'Limite atingido' : `${remaining} lead${remaining !== 1 ? 's' : ''} restante${remaining !== 1 ? 's' : ''}`}
              </Typography>
            </Box>
            <Typography variant="h4" sx={{
              color: isAtLimit ? '#f44336' : '#4caf50',
              fontWeight: 'bold'
            }}>
              {leadsAssignedThisHour}/5
            </Typography>
          </Box>
        </Box>

        {/* Alert Messages */}
        {isAtLimit ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Limite excedido!</strong> Você já atingiu o limite máximo de 5 leads por hora.
              Aguarde o próximo período para atribuir mais leads.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Você tem certeza que deseja atribuir este lead a você?
              Após esta atribuição, você terá <strong>{leadsAssignedThisHour + 1}</strong> de 5 leads nesta hora.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ mr: 1 }}
        >
          Cancelar
        </Button>
        {!isAtLimit && (
          <Button
            onClick={onConfirm}
            variant="contained"
            sx={{
              backgroundColor: '#ff9800',
              '&:hover': {
                backgroundColor: '#f57c00',
              },
            }}
          >
            <Assignment sx={{ mr: 1 }} />
            Confirmar Atribuição
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LeadAssignmentConfirmationModal;